import { FaHome, FaUsers, FaMapMarkerAlt, FaCar, FaUtensils, FaLock, FaArrowRight } from 'react-icons/fa';
import { motion } from 'framer-motion';
import SectionHeading from '../ui/SectionHeading';
import Card from '../ui/Card';

export default function PropertyOverview() {
  // Animation variants
  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2,
      },
    },
  };

  const item = {
    hidden: { opacity: 0, y: 30 },
    show: { 
      opacity: 1, 
      y: 0,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 12,
      },
    },
  };
  
  const iconAnimation = {
    hover: {
      scale: 1.1,
      rotate: [0, 5, -5, 0],
      transition: { duration: 0.5 }
    }
  };

  const features = [
    {
      title: "Property Type",
      description: "Entire 3-bedroom homestay with modern furnishings, spacious living areas, and all the comforts of home.",
      icon: <FaHome className="text-2xl" />
    },
    {
      title: "Capacity",
      description: "Comfortably hosts up to 10 guests with 1 king bed and 2 queen beds across 3 bedrooms, perfect for gatherings.",
      icon: <FaUsers className="text-2xl" />
    },
    {
      title: "Location",
      description: "Prime location near UiTM Segamat (5 min), with easy access to shops, restaurants, and local attractions.",
      icon: <FaMapMarkerAlt className="text-2xl" />
    },
    {
      title: "Parking",
      description: "Secure private parking for up to 4 vehicles within our gated compound for your convenience.",
      icon: <FaCar className="text-2xl" />
    },
    {
      title: "Facilities",
      description: "Fully-equipped kitchen, Wi-Fi, smart TV, washing machine, air-conditioning, and thoughtful amenities.",
      icon: <FaUtensils className="text-2xl" />
    },
    {
      title: "Security",
      description: "Peace of mind with gated entry, CCTV surveillance, and convenient self check-in system.",
      icon: <FaLock className="text-2xl" />
    }
  ];

  return (
    <>
      <SectionHeading 
        title="Property Overview" 
        subtitle="Everything you need to know about our comfortable homestay"
      />
      
      <motion.div 
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8"
        variants={container}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: "-100px" }}
      >
        {features.map((feature, index) => (
          <motion.div key={index} variants={item}>
            <Card className="h-full overflow-hidden group">
              <div className="p-1">  {/* Added padding inside the card */}
                <div className="flex flex-col h-full">
                  <motion.div 
                    className="flex-shrink-0 h-14 w-14 flex items-center justify-center rounded-full bg-gradient-to-br from-blue-400 to-blue-600 text-white shadow-md mb-4"
                    whileHover="hover"
                    variants={iconAnimation}
                  >
                    {feature.icon}
                  </motion.div>
                  
                  <div className="flex-grow">
                    <h3 className="text-xl font-semibold text-gray-900 mb-3 flex items-center">
                      {feature.title}
                      <motion.span 
                        className="ml-2 opacity-0 group-hover:opacity-100 text-blue-500"
                        initial={{ x: -5, opacity: 0 }}
                        animate={{ x: 0, opacity: 0 }}
                        whileHover={{ x: 3 }}
                        transition={{ duration: 0.3 }}
                      >
                        <FaArrowRight size={14} />
                      </motion.span>
                    </h3>
                    
                    <p className="text-gray-600 leading-relaxed">
                      {feature.description}
                    </p>
                  </div>
                  
                  <motion.div 
                    className="w-full h-1 bg-gradient-to-r from-blue-400 to-blue-600 mt-4 rounded-full transform origin-left scale-x-0 group-hover:scale-x-100"
                    transition={{ duration: 0.3 }}
                  />
                </div>
              </div>
            </Card>
          </motion.div>
        ))}
      </motion.div>
    </>
  );
}
